import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  Form,
  Row,
  Col,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import IntegrationTag from '../common/IntegrationTag';
import DisplayText from '../common/text/DisplayText';
import DivisionSelector from '../common/inputs/DivisionSelector';

import CostcodeCategorySelector from './CostcodeCategorySelector';
import OnTraccrNumberInput from '../common/inputs/OnTraccrNumberInput';
import CustomFields from '../common/customFields/CustomFields';
import { getCostcodeCustomData, getCostcodeCustomFieldTemplate } from './state/costcodes.actions';
import { doesNumberExist } from '../helpers/helpers';
import { EQUIPMENT_CATEGORY_ID } from '../helpers/costcodeHelpers';
import { COSTCODE_FORM_RULES } from './costcodes.constants';

const validateKey = (id, costcodes, key) => (_rule, value) => {
  const trimmedValue = value ? value.trim().toLowerCase() : value;
  if (costcodes.some((cc) => (cc[key] ?? '').trim().toLowerCase() === trimmedValue && id !== cc.id)) {
    return Promise.reject(new Error(`Costcode ${key} ${value} already exists`));
  }

  return Promise.resolve();
};

export default function CostCodeEditView({
  form,
  formProps,
  isProjectSpecific = false,
  isProjectAdd = false,
  errors,
  customFields,
  setCustomFields,
}) {
  const dispatch = useDispatch();
  const {
    id,
    name,
    code,
    divisionId,
    description,
    hourlyWage,
    dailyWage,
    wageAdjustment,
    wageMultiplier,
    hourlyBillingRate,
    dailyBillingRate,
    editing = false,
    costcodes = [],
    intuitId,
    procoreId,
    sageId,
    initialCode = code,
    key = 'editcode',
    categoryIds = [],
  } = formProps;

  const {
    customData,
    customDataFiles,
    customFieldTemplate,
  } = useSelector((state) => state.costcodes);

  const { formRef, isAdd } = form;

  const [formValues, setFormValues] = useState(0);
  const [shouldUpdate, forceUpdate] = useReducer((x) => x + 1, 0);
  const [selectedDivisionId, setSelectedDivisionId] = useState(divisionId);

  const onValuesChange = useCallback((_, values) => {
    setFormValues(values);
  }, []);

  useEffect(() => {
    dispatch(getCostcodeCustomFieldTemplate());
    dispatch(getCostcodeCustomData(id));
  }, [id]);

  useEffect(() => {
    forceUpdate();
  }, [customData]);

  useEffect(() => {
    setCustomFields(customFieldTemplate?.sections);
  }, [customFieldTemplate]);

  useEffect(() => {
    if (formRef?.current) {
      setFormValues(formRef.current.getFieldsValue());
    }
  }, [formRef]);

  const customFieldDivisions = useMemo(() => [selectedDivisionId], [selectedDivisionId]);

  const isOnlyOnEquipment = formValues?.categoryIds?.length === 1
    && formValues?.categoryIds?.includes(EQUIPMENT_CATEGORY_ID);

  const hasEquipment = formValues?.categoryIds
    ?.includes(EQUIPMENT_CATEGORY_ID);

  return (
    <Form
      className="costcode-form-label"
      layout="vertical"
      ref={formRef}
      initialValues={{
        name,
        code: initialCode,
        description,
        divisionId,
        categoryIds,
        hourlyWage,
        dailyWage,
        wageAdjustment,
        wageMultiplier,
        hourlyBillingRate,
        dailyBillingRate,
      }}
      key={key}
      onValuesChange={onValuesChange}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="code"
            label="Cost Code"
            style={{ marginBottom: 20 }}
            rules={[
              { required: isAdd, message: 'Please enter a cost code' },
              { max: COSTCODE_FORM_RULES.codeMaxLength, message: `Code cannot exceed ${COSTCODE_FORM_RULES.codeMaxLength} characters ` },
              { validator: validateKey(id, costcodes, 'code') },
            ]}
          >
            {editing ? (
              <OnTraccrTextInput
                className="costcode-form-input"
                style={{ width: '100%' }}
              />
            ) : <DisplayText title={code} />}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Name"
            style={{ marginBottom: 20 }}
            rules={[
              { required: isAdd, message: 'Please enter a name' },
              { validator: validateKey(id, costcodes, 'name') },
            ]}
          >
            {editing ? (
              <OnTraccrTextInput
                className="costcode-form-input"
                style={{ width: '100%' }}
              />
            ) : <DisplayText title={name} />}
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="description"
        label="Description"
        style={{ marginBottom: 20 }}
        rules={[{ required: isAdd, message: 'Please enter a description' }]}
      >
        {editing ? (
          <OnTraccrTextInput
            textarea
            className="costcode-form-input"
            style={{ width: '100%' }}
            rows={3}
          />
        ) : <DisplayText title={description} />}
      </Form.Item>
      <Form.Item
        name="categoryIds"
        key="categoryIds"
        label="Categories"
        style={{ marginBottom: 20, width: '100%' }}
        valuePropName="categoryIds"
        rules={[{ required: true, message: 'Please select at least one category' }]}
      >
        <CostcodeCategorySelector
          displayMode={!editing}
          useRedesign
        />
      </Form.Item>
      {!editing && !isProjectAdd && (
        <Form.Item
          name="integrations"
          key="integrations"
          label="Integrations"
          style={{ marginBottom: 20, width: '100%' }}
        >
          <IntegrationTag id={intuitId} title="QuickBooks" />
          <IntegrationTag id={procoreId} title="Procore" />
          <IntegrationTag id={sageId} title="Sage" />
        </Form.Item>
      )}
      {!isProjectAdd && (
        <Form.Item
          name="divisionId"
          key="divisionId"
          label="Division"
          style={{ marginBottom: 20, width: '100%' }}
          valuePropName="divisionId"
        >
          <DivisionSelector
            displayMode={isProjectSpecific || !editing}
            divisionId={selectedDivisionId}
            onChange={setSelectedDivisionId}
          />
        </Form.Item>
      )}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="hourlyWage"
            label={isOnlyOnEquipment ? 'Hourly Cost' : 'Hourly Wage'}
            key="hourlyWage"
            style={{ marginBottom: 20, width: '100%' }}
          >
            {editing ? (
              <OnTraccrNumberInput
                className="costcode-form-input"
                style={{ width: '100%' }}
                disabled={
                  (doesNumberExist(formValues?.dailyWage) && !hasEquipment)
                  || doesNumberExist(formValues?.wageMultiplier)
                  || doesNumberExist(formValues?.wageAdjustment)
                }
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                step={0.01}
                precision={2}
                min={0}
              />
            ) : <DisplayText title={hourlyWage ?? '-'} />}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="dailyWage"
            label={isOnlyOnEquipment ? 'Daily Cost' : 'Daily Wage'}
            key="dailyWage"
            style={{ marginBottom: 20, width: '100%' }}
          >
            {editing ? (
              <OnTraccrNumberInput
                className="costcode-form-input"
                style={{ width: '100%' }}
                disabled={
                  (doesNumberExist(formValues?.hourlyWage) && !hasEquipment)
                  || doesNumberExist(formValues?.wageMultiplier)
                  || doesNumberExist(formValues?.wageAdjustment)
                }
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                step={0.01}
                precision={2}
                min={0}
              />
            ) : <DisplayText title={dailyWage ?? '-'} />}
          </Form.Item>
        </Col>
      </Row>
      {!isOnlyOnEquipment && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="wageAdjustment"
              label="Wage Adjustment"
              key="wageAdjustment"
              style={{ marginBottom: 20, width: '100%' }}
            >
              {editing ? (
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  style={{ width: '100%' }}
                  disabled={
                    doesNumberExist(formValues?.wageMultiplier)
                    || doesNumberExist(formValues?.hourlyWage)
                    || doesNumberExist(formValues?.dailyWage)
                  }
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  step={0.01}
                  precision={2}
                />
              ) : <DisplayText title={wageAdjustment ?? '-'} />}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="wageMultiplier"
              label="Wage Multiplier"
              key="wageMultiplier"
              style={{ marginBottom: 20, width: '100%' }}
            >
              {editing ? (
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  style={{ width: '100%' }}
                  disabled={
                    doesNumberExist(formValues?.hourlyWage)
                    || doesNumberExist(formValues?.dailyWage)
                    || doesNumberExist(formValues?.wageAdjustment)
                  }
                  step={0.01}
                  precision={2}
                  min={0}
                />
              ) : <DisplayText title={wageMultiplier ?? '-'} />}
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="hourlyBillingRate"
            label="Hourly Billing Rate"
            key="hourlyBillingRate"
            style={{ marginBottom: 20, width: '100%' }}
          >
            {editing ? (
              <OnTraccrNumberInput
                className="costcode-form-input"
                style={{ width: '100%' }}
                disabled={doesNumberExist(formValues?.dailyBillingRate)}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                step={0.01}
                precision={2}
                min={0}
              />
            ) : <DisplayText title={hourlyBillingRate ?? '-'} />}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="dailyBillingRate"
            label="Daily Billing Rate"
            key="dailyBillingRate"
            style={{ marginBottom: 20, width: '100%' }}
          >
            {editing ? (
              <OnTraccrNumberInput
                className="costcode-form-input"
                style={{ width: '100%' }}
                disabled={doesNumberExist(formValues?.hourlyBillingRate)}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                step={0.01}
                precision={2}
                min={0}
              />
            ) : <DisplayText title={dailyBillingRate ?? '-'} />}
          </Form.Item>
        </Col>
      </Row>
      { !!customFields?.length && (
        <Form.Item
          name="customData"
          style={{ marginBottom: 20 }}
        >
          <CustomFields
            key={`${id}${shouldUpdate}`} // Need to force re-render
            initialValues={customData}
            customFields={customFields}
            divisions={customFieldDivisions}
            errors={errors}
            fileMap={customDataFiles}
            isDisplay={!editing}
          />
        </Form.Item>
      )}
    </Form>
  );
}

/* eslint-disable react/forbid-prop-types */
CostCodeEditView.propTypes = {
  form: PropTypes.shape({
    formRef: PropTypes.shape({
      current: PropTypes.shape({
        getFieldsValue: PropTypes.func,
      }),
    }),
    isAdd: PropTypes.bool,
  }).isRequired,
  formProps: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    code: PropTypes.string,
    divisionId: PropTypes.string,
    categoryIds: PropTypes.array,
    description: PropTypes.string,
    hourlyWage: PropTypes.number,
    dailyWage: PropTypes.number,
    wageAdjustment: PropTypes.number,
    wageMultiplier: PropTypes.number,
    hourlyBillingRate: PropTypes.number,
    dailyBillingRate: PropTypes.number,
    editing: PropTypes.bool,
    costcodes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      code: PropTypes.string,
      divisionId: PropTypes.number,
      categoryIds: PropTypes.array,
      description: PropTypes.string,
      hourlyWage: PropTypes.number,
      dailyWage: PropTypes.number,
      wageAdjustment: PropTypes.number,
      wageMultiplier: PropTypes.number,
      hourlyBillingRate: PropTypes.number,
      dailyBillingRate: PropTypes.number,
    })),
    intuitId: PropTypes.string,
    procoreId: PropTypes.string,
    sageId: PropTypes.string,
    initialCode: PropTypes.string,
    key: PropTypes.string,
  }).isRequired,
  isProjectSpecific: PropTypes.bool,
  isProjectAdd: PropTypes.bool,
  errors: PropTypes.shape({}),
  customFields: PropTypes.arrayOf(PropTypes.shape({})),
  setCustomFields: PropTypes.func.isRequired,
};

CostCodeEditView.defaultProps = {
  isProjectSpecific: false,
  isProjectAdd: false,
  errors: {},
  customFields: [],
};
